'use client'

// His Gray-Scott shader, running on the town's dead billboards.
//
// He wrote it in JavaScript against a 2D canvas, because that is what the
// cafe's browser gives him: four nested loops over a 256x144 grid, eight
// times a frame, then a fillRect per cell. On his own page that is about
// three hundred thousand fillRect calls a second and it is fine, because
// nothing else is on the page.
//
// In the town it would be a disaster, so this is the same program moved
// onto the GPU. His arithmetic, his constants, his palette, his edge rule
// (a cell on the border reads itself as its missing neighbour). The only
// things that changed are the things that had to.
//
// What "optimised" actually means here, in the order the costs matter:
//
//   Nothing runs when nobody can see it. Both boards are frustum and
//   distance tested every frame, and if neither is on screen there are
//   zero dispatches. Most of the time in this world, that is the case.
//   This is worth more than every other line in the file.
//
//   One simulation, two boards. The grid is the picture, not the surface,
//   so the museum board and the salon board read the same buffers.
//
//   One compute pass a tick, not eight. renderer.compute() takes an array
//   and encodes it into a single pass, and WebGPU orders dispatches within
//   a pass, which is the barrier a reaction-diffusion step needs between
//   reading its neighbours and writing itself.
//
//   A fixed tick. His page steps on every animation frame, so the pattern
//   crawls on a slow machine and races on a fast one. A billboard has to
//   look the same to everybody, so his eight steps run at a fixed 60 a
//   second with a ceiling of three ticks in one frame: a tab that comes
//   back from the background catches up by a few frames rather than
//   spiralling through a minute of simulation in one go.
//
//   An even number of steps, always, so the newest state lands back in
//   the same buffer every tick and the material's node graph is compiled
//   once for the life of the page. Swapping which buffer the fragment
//   shader reads would mean rebuilding it, which is a stall you can see.
//
//   The material is MeshStandardNodeMaterial, like every other surface on
//   these boards. I had it as MeshBasicNodeMaterial first, reasoning that
//   a screen emits its own light and does not need the lit pipeline. It
//   rendered correctly and it was wrong: the town's dither is driven by
//   luminance (rendererDither, shadowThreshold 0.25), so an unlit surface
//   sits at full brightness, never enters the dither band, and is the one
//   thing in the world with no grain on it. Sam spotted it from a
//   screenshot. The lighting graph is what every other surface here pays
//   and it is what makes this one belong.
//
//   No React state anywhere near the loop. One useFrame, refs only, no
//   re-render after the pipelines are built.
//
// It loops, which his page does not. At his F and k the reaction settles:
// measured headless, coverage plateaus at 43.6% and the per-step movement
// collapses between tick 550 and tick 625, so ten seconds in the board
// would be a still image. The restart is the board's, not his: same seed,
// same rules, same picture every time round, with the screen dipping to
// his base colour across the join so the cut reads as a screen rather
// than as a fault.
//
// Off WebGPU there is no compute, so this stands down and the boards keep
// the blank placeholder they have now. Same rule the dither pass follows.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn, instancedArray, instanceIndex, float, vec2, uv, step, mix, color, uniform,
} from 'three/tsl'
import { ditherMaterial } from '../effects/ditherRegistry'
import {
  GRAY_SCOTT, PALETTE, TICKS_A_SECOND, MAX_TICKS_A_FRAME, LOOP_TICKS, FADE_TICKS,
  loopFade, cellCount,
} from '../../../lib/ads/deadScreens'

const TICK_MS = 1 / TICKS_A_SECOND

// How far away the boards stop being worth simulating. Past this they are
// a handful of pixels and a still frame reads identically, and the far
// one is 60 metres from the near one so they are rarely both in play.
const SEE_IT_FROM = 70

/**
 * One step of his update(), as a compute kernel.
 *
 * Index arithmetic is done in floats rather than with integer branches.
 * Every value involved is well under 2^24, so the division is exact, and
 * min/max against the grid edge gives his "a border cell is its own
 * missing neighbour" rule without a conditional in the kernel.
 */
function stepKernel(src, dst) {
  const { gridW, gridH, Du, Dv, F, k } = GRAY_SCOTT
  const W = float(gridW)
  const H = float(gridH)

  return Fn(() => {
    const i = instanceIndex.toFloat()
    const y = i.div(W).floor()
    const x = i.sub(y.mul(W))

    const at = (px, py) => py.mul(W).add(px).toUint()
    const left = at(x.sub(1).max(0), y)
    const right = at(x.add(1).min(W.sub(1)), y)
    const up = at(x, y.sub(1).max(0))
    const down = at(x, y.add(1).min(H.sub(1)))

    const c = src.element(instanceIndex).toVar()
    const lap = src.element(left)
      .add(src.element(right))
      .add(src.element(up))
      .add(src.element(down))
      .sub(c.mul(4))

    // uc * vc * vc, the term both equations share.
    const reaction = c.x.mul(c.y).mul(c.y)
    const du = lap.x.mul(Du).sub(reaction).add(float(1).sub(c.x).mul(F))
    const dv = lap.y.mul(Dv).add(reaction).sub(c.y.mul(F + k))

    dst.element(instanceIndex).assign(vec2(c.x.add(du), c.y.add(dv)).clamp(0, 1))
  })().compute(gridW * gridH)
}

/** His setup(): u is 1 everywhere, with a square of seed in the middle. */
function seedKernel(a, b) {
  const { gridW, gridH, seedRadius, seedU, seedV } = GRAY_SCOTT
  const W = float(gridW)
  const cx = gridW >> 1
  const cy = gridH >> 1

  return Fn(() => {
    const i = instanceIndex.toFloat()
    const y = i.div(W).floor()
    const x = i.sub(y.mul(W))

    // 1 inside his 21 by 21 square, 0 outside, without a branch.
    const inX = step(float(cx - seedRadius), x).mul(step(x, float(cx + seedRadius)))
    const inY = step(float(cy - seedRadius), y).mul(step(y, float(cy + seedRadius)))
    const seeded = inX.mul(inY)

    const cell = mix(vec2(1, 0), vec2(seedU, seedV), seeded).toVar()
    a.element(instanceIndex).assign(cell)
    b.element(instanceIndex).assign(cell)
  })().compute(gridW * gridH)
}

/**
 * His render(): four colours, thresholded, later ones painted over
 * earlier ones. One texel per cell and no filtering, because his version
 * draws a rectangle per cell and the chunk is the look.
 */
function paletteNode(buffer, fade) {
  const { gridW, gridH } = GRAY_SCOTT
  const W = float(gridW)
  const H = float(gridH)

  return Fn(() => {
    const st = uv()
    const x = st.x.mul(W).floor().clamp(0, W.sub(1))
    const y = st.y.mul(H).floor().clamp(0, H.sub(1))
    const cell = buffer.element(y.mul(W).add(x).toUint()).toVar()

    const [base, ...over] = PALETTE
    let out = color(base.hex)
    for (const band of over) {
      const on = band.on === 'v' ? cell.y : cell.x
      out = mix(out, color(band.hex), step(float(band.above), on))
    }
    // Across the loop's join the board falls back to his own base colour
    // rather than to black: a screen going blank, not a hole in the town.
    return mix(color(base.hex), out, fade)
  })()
}

/**
 * The shared material for every dead screen, plus the tick that drives it.
 *
 * Returns { material, attach } where attach(mesh) puts a board under the
 * visibility test. material is null until the pipelines are built, and
 * stays null off WebGPU, so callers fall back to the blank placeholder.
 */
export function useDeadScreen() {
  const gl = useThree((st) => st.gl)
  const camera = useThree((st) => st.camera)
  const [material, setMaterial] = useState(null)

  // Everything the frame loop touches lives here. A ref rather than state
  // on purpose: a board coming into view must not re-render the tree.
  const rig = useRef({ batches: null, reseed: null, fade: null, boards: new Set(), owed: 0, tick: 0, seeded: false })

  const webgpu = gl?.backend?.isWebGPUBackend === true

  const built = useMemo(() => {
    if (!webgpu) return null
    const n = cellCount()
    const a = instancedArray(n, 'vec2')
    const b = instancedArray(n, 'vec2')

    // His eight steps, alternating, so the last write of every tick lands
    // in `a`. The material reads `a` and never has to change.
    const aToB = stepKernel(a, b)
    const bToA = stepKernel(b, a)
    const steps = []
    for (let i = 0; i < GRAY_SCOTT.steps; i++) steps.push(i % 2 === 0 ? aToB : bToA)
    // One array per catch-up depth, built here rather than in the frame
    // loop: the loop must not allocate, and there are only three of them.
    const batches = []
    for (let n = 1; n <= MAX_TICKS_A_FRAME; n++) {
      batches.push(Array.from({ length: n }, () => steps).flat())
    }

    // Starts LIT. At 0 the board renders as his flat base colour, and the
    // frame loop is the only thing that moves it: a board that is off
    // screen, or waiting for its first tick, would hang there as a dead
    // navy rectangle. Which it did, for as long as it took to notice.
    const fade = uniform(1)
    const mat = new THREE.MeshStandardNodeMaterial()
    mat.colorNode = paletteNode(a, fade)
    // The same matte the boards' own material uses: printed board, not
    // plastic. His colours are already vivid enough to read across a
    // street without a specular highlight helping.
    mat.roughness = 0.92
    mat.metalness = 0
    // Double-sided for the same reason the boards' own material is: the
    // salon screen faces away from the salon, and from where he actually
    // stands a front-sided one is not there at all.
    mat.side = THREE.DoubleSide
    // Lit and tone mapped with everything else, which is why his neon
    // comes out a shade softer here than on his own page. The one
    // surface in the town that skipped either would read as a hole cut
    // in the render rather than as a board on a street.
    // At birth, like every other surface here. A material that skips this
    // renders outside the dither pass and reads as a hole in the town.
    ditherMaterial(mat)

    const seed = seedKernel(a, b)
    // The wrapping frame: seed both buffers, then one tick of his steps,
    // in one pass. Catching up through a restart would be catching up on
    // a pattern that no longer exists.
    return { a, b, steps, batches, fade, seed, reseed: [seed, ...steps], mat }
  }, [webgpu])

  useEffect(() => {
    if (!built) return
    let alive = true
    const r = rig.current
    r.batches = built.batches
    r.reseed = built.reseed
    r.fade = built.fade
    r.owed = 0
    // Not 0: tick 0 is the loop's join, and opening the page mid-blink
    // is not what a screen does. Past the fade-in, so the first thing
    // anybody sees is a lit board, and the first join is a full loop away.
    r.tick = FADE_TICKS
    r.seeded = false

    // Compiled before anything is shown. computeAsync so the first frame
    // is not the one that waits for a pipeline.
    gl.computeAsync(built.seed)
      .then(() => {
        if (!alive) return
        r.seeded = true
        setMaterial(built.mat)
      })
      .catch(() => { /* no shader, the board keeps its placeholder */ })

    return () => {
      alive = false
      r.batches = null
      r.reseed = null
      r.fade = null
      r.seeded = false
      setMaterial(null)
      built.mat.dispose()
    }
  }, [built, gl])

  const attach = useCallback((mesh) => {
    const r = rig.current
    if (!mesh) return () => {}
    r.boards.add(mesh)
    return () => { r.boards.delete(mesh) }
  }, [])

  const frustum = useMemo(() => new THREE.Frustum(), [])
  const projScreen = useMemo(() => new THREE.Matrix4(), [])
  const here = useMemo(() => new THREE.Vector3(), [])

  useFrame((_, delta) => {
    const r = rig.current
    if (!r.batches || !r.seeded || r.boards.size === 0) return
    // No document.hidden check. A backgrounded tab stops getting
    // animation frames, so this loop is not running anyway, and the flag
    // is true in surfaces that are still drawing (an offscreen or
    // embedded view), where it would freeze the boards for no reason.

    // The whole optimisation, in six lines: is either board actually in
    // front of somebody. A billboard is a flat quad with a bounding
    // sphere the loader already computed, so this is two sphere tests.
    let seen = false
    projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
    frustum.setFromProjectionMatrix(projScreen)
    for (const mesh of r.boards) {
      if (!mesh.visible) continue
      here.setFromMatrixPosition(mesh.matrixWorld)
      if (here.distanceTo(camera.position) > SEE_IT_FROM) continue
      if (!frustum.intersectsObject(mesh)) continue
      seen = true
      break
    }
    if (!seen) {
      // Not paused mid-pattern: time that passed while it was off screen
      // is time the reaction did not run, and nobody can tell. Dropping
      // the debt is what stops it from catching up in one violent burst
      // the moment somebody turns round.
      r.owed = 0
      return
    }

    r.owed += delta
    let ticks = Math.floor(r.owed / TICK_MS)
    if (ticks <= 0) return
    if (ticks > MAX_TICKS_A_FRAME) ticks = MAX_TICKS_A_FRAME
    r.owed -= ticks * TICK_MS
    if (r.owed > TICK_MS * MAX_TICKS_A_FRAME) r.owed = 0

    // Where the loop is, before and after. A frame that crosses the join
    // starts the pattern again instead of catching up through it.
    const was = Math.floor(r.tick / LOOP_TICKS)
    r.tick += ticks
    const wrapped = Math.floor(r.tick / LOOP_TICKS) > was
    if (wrapped) r.tick = r.tick % LOOP_TICKS
    r.fade.value = loopFade(r.tick)

    // One pass, however many ticks are owed: the array is encoded into a
    // single compute pass rather than one per step.
    gl.compute(wrapped ? r.reseed : r.batches[ticks - 1])
  })

  // Memoised, because callers put it in an effect's dependency list and
  // a fresh object every render would tear down and rebuild every board
  // material on every render of the tree above it.
  return useMemo(() => ({ material, attach }), [material, attach])
}

export default useDeadScreen
